import { flip, offset, size, useFloating } from '@floating-ui/react';
import { Listbox, Transition } from '@headlessui/react';
import { Column } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import cn from 'classnames';
import { CSSProperties, Fragment, forwardRef, useMemo, useRef } from 'react';
import { MdExpandMore } from 'react-icons/md';
import { Option } from './CurrencySelect';

interface ColumnFilterSelectProps {
    column: Column<any, unknown>;
    options: Option[];
    selected?: Option;
    onChange?: (e: Option) => void;
}

export interface ListItemProps {
    index: number;
    style: CSSProperties;
}

const dropDownClassName =
    'w-full bg-brand-background-dark rounded-sm py-1 text-xs border border-brand-neutral-700 shadow-lg shadow-background-dark';

const ColumnFilterSelect = forwardRef((props: ColumnFilterSelectProps, _ref) => {
    const { column, options, selected, onChange } = props;

    const middleware = useMemo(() => {
        return [
            size({
                apply({ rects, elements }) {
                    Object.assign(elements.floating.style, {
                        width: `${rects.reference.width}px`
                    });
                }
            }),
            offset(8),
            flip()
        ];
    }, []);

    const { refs, floatingStyles } = useFloating({
        strategy: 'fixed',
        placement: 'bottom-start',
        middleware
    });

    return (
        <Listbox value={selected?.value} onChange={onChange as any}>
            <div
                ref={refs.setReference}
                className={cn('relative', {
                    [`min-w-[${column.columnDef.minSize}px]`]: column.columnDef.minSize,
                    [`w-[${column.columnDef.size}px]`]: column.columnDef.size,
                    [`max-w-[${column.columnDef.maxSize}px]`]: column.columnDef.maxSize,
                    'w-full': !column.columnDef.size,
                    [`min-w-[150px]`]: !column.columnDef.minSize
                })}>
                <Listbox.Button className="flex items-center justify-between relative w-full border border-brand-neutral-700 text-left p-0 px-2 text-2xs leading-5 rounded-sm">
                    <span className="block truncate whitespace-normal">{selected?.label || ''}</span>
                    <span className="pointer-events-none ">
                        <MdExpandMore className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    </span>
                </Listbox.Button>
                {/* temporary shadow to the dropdown to differentiate */}
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <Listbox.Options ref={refs.setFloating} style={floatingStyles} className="z-50">
                        {options && options.length > 0 ? (
                            <VirtualizedList
                                items={options}
                                selected={selected}
                                dropDownClassName={dropDownClassName}
                                {...props}
                            />
                        ) : (
                            <div className={dropDownClassName}>No Available Options</div>
                        )}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    );
});

ColumnFilterSelect.displayName = 'ColumnFilterSelect';

export default ColumnFilterSelect;

interface VirtualizedListProps extends ColumnFilterSelectProps {
    items: Option[];
    selected?: Option;
    dropDownClassName: string;
}

function VirtualizedList({ items, selected, dropDownClassName }: VirtualizedListProps) {
    const parentRef = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer({
        count: items?.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 32,
        overscan: 5
    });

    return (
        <div ref={parentRef} className={dropDownClassName}>
            <div
                style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative'
                }}>
                {rowVirtualizer.getVirtualItems().map((virtualRow: any) => {
                    const option = items?.[virtualRow.index];
                    return (
                        <Listbox.Option
                            key={virtualRow.index}
                            title={option.label}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualRow.start}px)`
                            }}
                            className={({ active }) =>
                                cn(`relative cursor-pointer select-none p-2`, {
                                    'bg-primary': active,
                                    'text-brand-text': selected?.value === option.value
                                })
                            }
                            value={items?.[virtualRow.index]}>
                            <span className={cn(`block truncate whitespace-normal text-start`)}>
                                {items?.[virtualRow.index]?.label}
                            </span>
                        </Listbox.Option>
                    );
                })}
            </div>
        </div>
    );
}
